import { db } from '../../../context/Firebase';
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, query, orderBy, startAfter, limit } from 'firebase/firestore';
import ArticleRepository from '../../../domain/repositories/ArticleRepository';
import Article from '../../../domain/entities/Article';

class FirebaseArticleRepository extends ArticleRepository {
    constructor() {
        super();
        this.collectionRef = collection(db, 'articles');
    }

    /**
     * Converts a Firestore document snapshot to an Article entity.
     * @param {DocumentSnapshot} docSnap - The Firestore document snapshot.
     * @returns {Article}
     */
    _toArticleEntity(docSnap) {
        const data = docSnap.data();
        return new Article({
            id: docSnap.id,
            category: data.category,
            title: data.title,
            link: data.link,
            linkName: data.linkName,
            description: data.description,
            imageUrl: data.imageUrl,
            imageType: data.imageType,
            createdAt: data.createdAt.toDate(),
            isTrending: data.isTrending,
            likes: data.likes || [],
        });
    }

    async getArticles(limitCount, startAfterId) {
        let q = query(this.collectionRef, orderBy('createdAt', 'desc'));

        if (startAfterId) {
            const startAfterDoc = await getDoc(doc(db, 'articles', startAfterId));
            if (startAfterDoc.exists()) {
                q = query(q, startAfter(startAfterDoc));
            }
        }

        if (limitCount) {
            q = query(q, limit(limitCount));
        }

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(this._toArticleEntity);
    }

    async getArticleById(id) {
        const docRef = doc(db, 'articles', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return this._toArticleEntity(docSnap);
        }
        return null;
    }

    async createArticle(articleData) {
        const newArticleData = {
            category: articleData.category,
            title: articleData.title,
            link: articleData.link || '',
            linkName: articleData.linkName || '',
            description: articleData.description,
            imageUrl: articleData.imageUrl || null,
            imageType: articleData.imageType || 'banner',
            createdAt: new Date(),
            isTrending: articleData.isTrending || false,
            likes: [],
        };
        const docRef = await addDoc(this.collectionRef, newArticleData);
        return new Article({ ...newArticleData, id: docRef.id });
    }

    async updateArticle(id, updates) {
        const docRef = doc(db, 'articles', id);
        await updateDoc(docRef, updates);
    }

    async deleteArticle(id) {
        const docRef = doc(db, 'articles', id);
        await deleteDoc(docRef);
    }

    async getArticleCategories() {
        try {
            const querySnapshot = await getDocs(collection(db, 'quick-reads-category'));
            return querySnapshot.docs.map(doc => doc.data().name);
        } catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    }
}

export default FirebaseArticleRepository;
